import { useQuery, useQueryClient, useQueries } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import { useLoadingScreen } from '../../Store/LoadingManager';

import { AllocateLicenseService, AssignLicenseService, DeleteLicenseService, EditLicenseService, GetLicenses, GetLicenseById, InvalidateLicenseService, BuyLicensePackageService, BuyLicenseService, GenerateLicenseService, GenerateLicenseInvoiceService } from "../../Api/Service/License/licenseService";

import QueryBuilderHelper from "../../Utilities/Helpers/QueryBuilderHelper";
import DropdownOptionsHelper from "../../Utilities/Helpers/DropdownOptionsHelper";

import { CachingEnum } from '../../Utilities/Enums/CachingEnum';

import { ErrorMessage, SuccessMessage } from "../../Constants/Messages";
import { LicenseStatus, LicenseStatusEnum } from "../../Utilities/Enums/StatusEnums";

export function useLicenseHook(q_queryKey: any, params: any, runInitialFetch: boolean = true) {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    let { loading, setLoading } = useLoadingScreen();
    const fileBaseUrl = `${process.env.REACT_APP_API_URL}/api/`;
    const DropdownList = [CachingEnum.LICENSE_TYPE, CachingEnum.LICENSE_PACKAGE_TYPE, CachingEnum.VALIDITY_LIST, CachingEnum.VALIDITY_TYPE_LIST, CachingEnum.DATASTORAGE_LIST, CachingEnum.NO_OF_USERS_LIST];

    // Initial fetch
    const {data, fetchStatus} = useQuery({
        queryKey: q_queryKey,
        queryFn: () => getLicensesList(params),
        enabled: runInitialFetch,
    });

    const { data: DropdownData, pending: dropdownFetchStatus } = useQueries({
        queries: DropdownList.map((item) => {
            return {
                queryKey: [item],
                queryFn: () => DropdownOptionsHelper.getLists(item),
            }
        }),
        combine: (results) => {
            const dictionary = Object.assign({}, ...results.map((x, index) => ({ [DropdownList[index]]: x.data })));
            return {
                data: dictionary,
                pending: results.some((result) => result.isPending),
            }
        },
    })

    const getLicensesList = async (params: any) => {
        let _data;
        let _legends;
        const responseData = await GetLicenses(QueryBuilderHelper.BuildQueryString(params));
        if (responseData?.isSuccess) {
            _data = responseData?.data;
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_FETCHING_DATA), { variant: 'error' })
        }

        _legends = finalizeLegend(_data);
        return { ..._legends, data: _data?.data}
    }

    const getLicenseById = async (id: string | number) => {
        setLoading(true);

        let _data;
        const responseData = await GetLicenseById(id);

        setLoading(false);
        
        return responseData?.data ?? null;
    }

    const finalizeLegend = (_data: any) => {
        const countDict = _data?.data?.metaData?.statusCount ?? {}
        const totalCount = countDict?.find((x: any | null) => x?.status == '-1')?.count ?? 0
        const legends = ([
            {
                ...LicenseStatus[LicenseStatusEnum.ALL],
                count: totalCount
            },
            {
                ...LicenseStatus[LicenseStatusEnum.USED],
                count: countDict?.find((x: any | null) => x?.status == LicenseStatusEnum.USED)?.count ?? 0
            },
            {
                ...LicenseStatus[LicenseStatusEnum.UNUSED],
                count: countDict?.find((x: any | null) => x?.status == LicenseStatusEnum.UNUSED)?.count ?? 0
            },
        ])
        return { legends: legends, totalCount: totalCount }
    }

    const buyLicensePackage = async (params: any) => {
        setLoading(true);
        const responseData = await BuyLicensePackageService(params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "License Package " + SuccessMessage.BOUGHT_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_BUYING_LICENSE_PACKAGE), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const buyLicense = async (params: any) => {
        setLoading(true);
        const responseData = await BuyLicenseService(params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "License " + SuccessMessage.BOUGHT_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_BUYING_LICENSE), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const generateLicense = async (params: any) => {
        setLoading(true);
        const responseData = await GenerateLicenseService(params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey);
            enqueueSnackbar(responseData?.data?.message ?? "License " + SuccessMessage.GENERATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_GENERATING_LICENSE), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const editLicenseById = async (id: any, params: any) => {
        setLoading(true);

        const responseData = await EditLicenseService(id, params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey)
            enqueueSnackbar(responseData?.data?.message ?? "License " + SuccessMessage.UPDATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_UPDATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    } 

    const allocateLicenseById = async (id: any, params: any) => {
        setLoading(true);

        const responseData = await AllocateLicenseService(id, params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey)
            enqueueSnackbar(responseData?.data?.message ?? "License " + SuccessMessage.ALLOCATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_ALLOCATING_LICENSE), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const assignLicenseById = async (id: any, params: any) => {
        setLoading(true);

        const responseData = await AssignLicenseService(id, params);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey)
            enqueueSnackbar(responseData?.data?.message ?? "License " + SuccessMessage.ASSIGNED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_ASSIGNING_LICENSE), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    } 

    const invalidateLicenseById = async (id: any) => {
        setLoading(true);

        const responseData = await InvalidateLicenseService(id);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey)
            enqueueSnackbar(responseData?.data?.message ?? "License " + SuccessMessage.INVALIDATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_UPDATING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const generateLicenseInvoiceById = async (id: any) => {
        setLoading(true);

        const responseData = await GenerateLicenseInvoiceService(id);

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey)
            // enqueueSnackbar(responseData?.data?.message ?? "License Invoice " + SuccessMessage.GENERATED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_GENERATING_LICENSE_INVOICE), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    }

    const deleteLicenseById = async (id: any, remarks?: any) => {
        setLoading(true);

        const responseData = await DeleteLicenseService(id, JSON.stringify({remarks: remarks ?? ""}));

        if (responseData?.isSuccess) {
            queryClient.invalidateQueries(q_queryKey)
            enqueueSnackbar(responseData?.data?.message ?? "License " + SuccessMessage.DELETED_SUCCESSFULLY, { variant: 'success' })
        } else {
            enqueueSnackbar(responseData?.message ?? (responseData?.data?.message ?? ErrorMessage.PROBLEM_DELETING_DATA), { variant: 'error' })
        }

        setLoading(false);

        return responseData;
    } 

    return { data, DropdownData, fetchStatus, dropdownFetchStatus, getLicenseById, buyLicensePackage, buyLicense, generateLicense, editLicenseById, allocateLicenseById, assignLicenseById, generateLicenseInvoiceById, invalidateLicenseById, deleteLicenseById }
}